/**
 * Choropleth styling for the county-level power outage / ERI GeoJSON layers.
 * Pure data + builders so the legend and the ArcGIS renderer are guaranteed to
 * agree.
 */

export type ChoroplethBreak = {
  /** Inclusive lower bound. */
  min: number;
  /**
   * Exclusive upper bound (ArcGIS treats maxValue as inclusive; we keep breaks
   * contiguous so it does not matter).
   */
  max: number;
  /** Hex fill color. */
  color: string;
  /** Legend text. */
  label: string;
};

export const POWER_OUTAGE_LAYER_ID = 'pratus-power-outages';
export const ERI_LAYER_ID = 'pratus-eri';

export const POWER_OUTAGE_FIELD = 'percentOut';
export const ERI_FIELD = 'eriMean';

/** Percent of tracked customers without power (0-100). */
export const POWER_OUTAGE_CLASS_BREAKS: readonly ChoroplethBreak[] = [
  { min: 0, max: 1, color: '#fee5d9', label: '< 1% out' },
  { min: 1, max: 5, color: '#fcae91', label: '1 – 5% out' },
  { min: 5, max: 15, color: '#fb6a4a', label: '5 – 15% out' },
  { min: 15, max: 40, color: '#de2d26', label: '15 – 40% out' },
  { min: 40, max: 100, color: '#a50f15', label: '≥ 40% out' },
];

/**
 * ERI (energy reliability index) is a 0-5 "storm level" forecast: the mean
 * scenario is a fractional level, so each class is centred on an integer level.
 * Labels carry the level's expected outage share / restoration time, matching
 * the legacy ERI table.
 */
export const ERI_CLASS_BREAKS: readonly ChoroplethBreak[] = [
  {
    min: 0,
    max: 0.5,
    color: '#ffffff',
    label: 'Level 0 · < 0.5% out · < 6 hrs',
  },
  {
    min: 0.5,
    max: 1.5,
    color: '#ffffb2',
    label: 'Level 1 · 0.5 – 5% out · 6 – 12 hrs',
  },
  {
    min: 1.5,
    max: 2.5,
    color: '#fecc5c',
    label: 'Level 2 · 6 – 10% out · 13 – 24 hrs',
  },
  {
    min: 2.5,
    max: 3.5,
    color: '#fd8d3c',
    label: 'Level 3 · 11 – 20% out · 1 – 2 days',
  },
  {
    min: 3.5,
    max: 4.5,
    color: '#f03b20',
    label: 'Level 4 · 21 – 40% out · 3 – 5 days',
  },
  {
    min: 4.5,
    max: 5,
    color: '#bd0026',
    label: 'Level 5 · ≥ 41% out · 6+ days',
  },
];

/** Find the class a value falls into (null when out of range or not a number). */
export function pickBreak(
  breaks: readonly ChoroplethBreak[],
  value: unknown
): ChoroplethBreak | null {
  if (typeof value !== 'number' || !Number.isFinite(value)) return null;
  const last = breaks[breaks.length - 1];
  if (last && value === last.max) return last;
  return breaks.find((b) => value >= b.min && value < b.max) ?? null;
}

export function hexToRgb(hex: string): [number, number, number] {
  const clean = hex.replace('#', '');
  const full =
    clean.length === 3
      ? clean
          .split('')
          .map((c) => c + c)
          .join('')
      : clean;
  const n = Number.parseInt(full, 16);
  return [(n >> 16) & 255, (n >> 8) & 255, n & 255];
}

export type ClassBreaksRendererJson = {
  type: 'class-breaks';
  field: string;
  defaultSymbol: {
    type: 'simple-fill';
    color: [number, number, number, number];
    outline: { color: [number, number, number, number]; width: number };
  };
  defaultLabel: string;
  classBreakInfos: Array<{
    minValue: number;
    maxValue: number;
    label: string;
    symbol: {
      type: 'simple-fill';
      color: [number, number, number, number];
      outline: { color: [number, number, number, number]; width: number };
    };
  }>;
};

/** Autocastable ArcGIS ClassBreaksRenderer JSON for a set of breaks on `field`. */
export function buildClassBreaksRenderer(
  breaks: readonly ChoroplethBreak[],
  field: string,
  options: {
    fillOpacity?: number;
    outlineColor?: [number, number, number, number];
  } = {}
): ClassBreaksRendererJson {
  const { fillOpacity = 0.65, outlineColor = [90, 90, 90, 0.6] } = options;
  return {
    type: 'class-breaks',
    field,
    defaultSymbol: {
      type: 'simple-fill',
      color: [200, 200, 200, 0.2],
      outline: { color: outlineColor, width: 0.5 },
    },
    defaultLabel: 'No data',
    classBreakInfos: breaks.map((b) => {
      const [r, g, bl] = hexToRgb(b.color);
      return {
        minValue: b.min,
        maxValue: b.max,
        label: b.label,
        symbol: {
          type: 'simple-fill',
          color: [r, g, bl, fillOpacity],
          outline: { color: outlineColor, width: 0.5 },
        },
      };
    }),
  };
}

/** Compact forecast-step label for the ERI select ("Tue Sep 15, 6:00 PM"). */
export function formatEriStepLabel(step: string): string {
  const date = new Date(step);
  if (Number.isNaN(date.getTime())) return step;
  return date.toLocaleString(undefined, {
    weekday: 'short',
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit',
  });
}
